"use client";

import * as React from "react";
import { format, differenceInYears } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { vi } from "date-fns/locale";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  useController,
  type UseControllerProps,
  type UseFormClearErrors,
  type UseFormSetError,
} from "react-hook-form";
import useTranslationApi from "@/lib/hooks/useTranslationApi";
import { toast } from "sonner";

type Props = UseControllerProps<any> & {
  placeholder?: string;
  name: string;
  control: any;
  setError?: UseFormSetError<any>;
  clearErrors?: UseFormClearErrors<any>;
  disabled?: boolean;
};

export function DatePicker({
  placeholder = "Chọn ngày",
  name,
  control,
  setError,
  clearErrors,
  disabled,
  rules,
}: Props) {
  const { t } = useTranslationApi(["error_age_greater_than"]);

  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      validate: (value) => {
        if (!fieldState.isDirty) return true;
        const yearsDifference = differenceInYears(new Date(), new Date(value));

        if (yearsDifference < 16) {
          return t("error_age_greater_than");
        } else {
          return true;
        }
      },
    },
  });

  const dateTime = new Date(field.value);

  return (
    <Popover
      onOpenChange={(open: boolean) => {
        if (open && !field.value) {
          field.onChange(new Date().getTime());
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={`w-full pb-1 flex items-center justify-between gap-2 border-b border-gray-400 ${
            !dateTime && "text-muted-foreground"
          }`}
        >
          <p className="flex-1 text-left">
            {field.value && fieldState.isDirty ? (
              <span className="text-base text-gray-500">
                {format(field.value, "dd/MM/yyyy")}
              </span>
            ) : (
              <span className="text-sm text-gray-400">{placeholder}</span>
            )}
          </p>
          <CalendarIcon className="mr-3 h-4 w-4 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
            locale={vi}

          mode="single"
          selected={dateTime}
          onSelect={(date, day) => {
            const dateTimeNew = day.getTime();
            field.onChange(dateTimeNew);
          }}
          onMonthChange={(data) => {
            const datePrev = dateTime.getDate();
            data.setDate(datePrev);
            if (data.getDate() === datePrev) {
              const dateTimeNew = data.getTime();
              field.onChange(dateTimeNew);
            } else {
              toast("Ngày sinh của bạn không còn hợp lệ vui lòng chọn lại!");
              data.setDate(1);
              data.setMonth(data.getMonth() - 1);
              const dateTimeNew = data.getTime();
              field.onChange(dateTimeNew);
            }
          }}
          defaultMonth={dateTime}
          initialFocus
          captionLayout="dropdown-buttons"
          fromYear={1900}
          toYear={new Date().getFullYear()}
        />
      </PopoverContent>
    </Popover>
  );
}
